// MCIP-26 — pure view-model logic for the trends screen. No DOM, no fetch:
// everything here is derivable from the /analytics payload and unit-tested.

/** 'YYYY-MM-DD' week_start → short 'Aug 3' style label (UTC, no TZ drift). */
export function weekLabel(weekStart) {
  const d = new Date(`${weekStart}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * Heatmap rows: families when the store recorded them, otherwise categories —
 * labeled, never silent. Rows keep the API's volume order (families[] /
 * categories[] are pre-sorted, most failures first), capped so a burst reads
 * as one hot row rather than 40 rows of noise (board rule: max 4 graphics,
 * each answering one question).
 */
export function heatmapModel(analytics, { maxRows = 8 } = {}) {
  const useFamilies = (analytics.families?.length || 0) > 0;
  const keys = (useFamilies ? analytics.families : analytics.categories) || [];
  const rows = keys.slice(0, maxRows).map((key) => ({
    key,
    cells: analytics.series.map((w) => (useFamilies ? w.by_family : w.by_category)?.[key] || 0),
  }));
  const max = rows.reduce((m, r) => Math.max(m, ...r.cells), 0);
  return {
    dimension: useFamilies ? 'family' : 'category',
    rows,
    max,
    truncated: Math.max(0, keys.length - maxRows),
  };
}

/** 0–4 intensity step for a heat cell (0 = empty, 4 = the hottest cell). */
export function heatStep(value, max) {
  if (!value || max <= 0) return 0;
  return Math.min(4, Math.max(1, Math.ceil((value / max) * 4)));
}

/**
 * Contiguous index ranges of weeks that saw backend incidents — the red bands
 * on the pass-rate chart. [startIdx, endIdx] inclusive, over series order.
 */
export function incidentBands(series) {
  const bands = [];
  let start = null;
  (series || []).forEach((w, i) => {
    if (w.incident_clusters > 0) {
      if (start === null) start = i;
    } else if (start !== null) {
      bands.push([start, i - 1]);
      start = null;
    }
  });
  if (start !== null) bands.push([start, (series || []).length - 1]);
  return bands;
}

/** Weeks that saw at least one deploy, with their versions — the chart flags. */
export function deployMarks(series) {
  return (series || [])
    .map((w, i) => ({ i, versions: w.deploy_versions || [] }))
    .filter((m) => m.versions.length > 0);
}
